import { type SocketMessage, socketMessageSchema } from '@wyn/contracts';
import { useEffect, useSyncExternalStore } from 'react';

export type SocketStatus = 'connecting' | 'live' | 'down';

/** API Gateway closes a socket after 10 quiet minutes; a ping every 5 keeps it open. */
const PING_MS = 5 * 60_000;
const MAX_BACKOFF_MS = 30_000;

/**
 * The browser's one socket. It reconnects with backoff (and at once when the tab returns or the
 * network comes back), follows the union of every page's topics, and hands each pushed message
 * to its listeners. While it is down, queries poll instead (see `pollInterval`).
 */
export class RealtimeClient {
  private ws: WebSocket | null = null;
  private current: SocketStatus = 'down';
  private running = false;
  private attempts = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private followTimer: ReturnType<typeof setTimeout> | null = null;
  private lastFollow = '';
  private readonly sources = new Map<string, readonly string[]>();
  private readonly listeners = new Set<(m: SocketMessage) => void>();
  private readonly statusListeners = new Set<() => void>();

  constructor(private readonly url: () => Promise<string | null>) {}

  get status(): SocketStatus {
    return this.current;
  }

  start() {
    if (this.running) return;
    this.running = true;
    addEventListener('online', this.wake);
    document.addEventListener('visibilitychange', this.wake);
    void this.connect();
  }

  stop() {
    this.running = false;
    removeEventListener('online', this.wake);
    document.removeEventListener('visibilitychange', this.wake);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.ws?.close();
    this.ws = null;
    this.setStatus('down');
  }

  /** Sets the topics one part of the page wants. The socket follows the union of all of them. */
  follow(owner: string, topics: readonly string[]) {
    if (topics.length === 0) this.sources.delete(owner);
    else this.sources.set(owner, topics);
    this.scheduleFollow();
  }

  onMessage(fn: (m: SocketMessage) => void) {
    this.listeners.add(fn);
    return () => void this.listeners.delete(fn);
  }

  subscribe = (fn: () => void) => {
    this.statusListeners.add(fn);
    return () => void this.statusListeners.delete(fn);
  };

  private setStatus(s: SocketStatus) {
    if (s === this.current) return;
    this.current = s;
    for (const fn of this.statusListeners) fn();
  }

  private wake = () => {
    if (!this.running || document.visibilityState === 'hidden' || this.ws) return;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    void this.connect();
  };

  private async connect() {
    if (!this.running || this.ws) return;
    this.setStatus('connecting');
    const url = await this.url().catch(() => null);
    if (!this.running || this.ws) return;
    if (!url) return this.retry();
    const ws = new WebSocket(url);
    this.ws = ws;
    ws.onopen = () => {
      this.attempts = 0;
      this.lastFollow = '';
      this.setStatus('live');
      this.sendFollow();
      this.pingTimer = setInterval(() => this.send({ type: 'ping' }), PING_MS);
    };
    ws.onmessage = (e) => {
      let json: unknown;
      try {
        json = JSON.parse(String(e.data));
      } catch {
        return;
      }
      const m = socketMessageSchema.safeParse(json);
      if (m.success) for (const fn of this.listeners) fn(m.data);
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = null;
      this.setStatus('down');
      this.retry();
    };
  }

  private retry() {
    if (!this.running || this.retryTimer) return;
    this.setStatus('down');
    const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** this.attempts++) * (0.75 + Math.random() * 0.5);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.connect();
    }, delay);
  }

  private send(msg: unknown) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  /** Pages change topics in bursts (a click, a navigation); follow once they settle. */
  private scheduleFollow() {
    if (this.followTimer) clearTimeout(this.followTimer);
    this.followTimer = setTimeout(() => this.sendFollow(), 250);
  }

  private sendFollow() {
    const topics = [...new Set([...this.sources.values()].flat())].sort();
    const key = topics.join(' ');
    if (key === this.lastFollow || this.ws?.readyState !== WebSocket.OPEN) return;
    this.lastFollow = key;
    this.send({ type: 'follow', topics });
  }
}

let client: RealtimeClient | null = null;

export function initRealtime(url: () => Promise<string | null>): RealtimeClient {
  client = new RealtimeClient(url);
  return client;
}

export function realtime(): RealtimeClient {
  if (!client) throw new Error('realtime not initialised');
  return client;
}

export function useSocketStatus(): SocketStatus {
  const c = realtime();
  return useSyncExternalStore(c.subscribe, () => c.status);
}

/** Follows topics while the calling component is mounted. */
export function useFollow(owner: string, topics: readonly string[]) {
  const key = topics.join(' ');
  useEffect(() => {
    realtime().follow(owner, key ? key.split(' ') : []);
    return () => realtime().follow(owner, []);
  }, [owner, key]);
}
